import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { AddPhotoAlternateOutlined, Close, DragIndicator, Refresh } from "@mui/icons-material";

const ACCEPT = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
    "image/gif": [".gif"],
};
const ACCEPTED_TYPES = Object.keys(ACCEPT);
const CONCURRENCY = 2;
const TILE = { xs: 88, sm: 104 };

interface ImageUploadGridProps<T> {
    /** Uploaded images, in display order. */
    items: T[];
    onChange: (items: T[]) => void;
    /** Uploads one file and resolves to the stored image. */
    upload: (file: File) => Promise<T>;
    getId: (item: T) => string;
    getThumbnail: (item: T) => string;
    getName?: (item: T) => string;
    maxFiles?: number;
    maxSizeMB?: number;
    disabled?: boolean;
    /** Paste images from the clipboard anywhere on the page (Ctrl/⌘+V). */
    enablePaste?: boolean;
    /** True while anything is still uploading — e.g. to hold the Save button. */
    onBusyChange?: (busy: boolean) => void;
    describeError?: (err: unknown) => string;
}

type PendingStatus = "queued" | "uploading" | "done" | "error";

interface Pending<T> {
    key: string;
    file: File;
    preview: string;
    status: PendingStatus;
    error?: string;
    result?: T;
}

let pendingSeq = 0;

/**
 * Pick, drop or paste images; each uploads immediately (two at a time,
 * kept in the order chosen), can be dragged to reorder (mouse, touch or
 * keyboard) and removed. Storage-agnostic: the caller supplies `upload`.
 */
export function ImageUploadGrid<T>({
    items,
    onChange,
    upload,
    getId,
    getThumbnail,
    getName,
    maxFiles = 10,
    maxSizeMB = 10,
    disabled = false,
    enablePaste = true,
    onBusyChange,
    describeError = () => "Upload failed",
}: ImageUploadGridProps<T>) {
    const [pending, setPending] = useState<Pending<T>[]>([]);
    const [messages, setMessages] = useState<string[]>([]);

    // Latest values for async callbacks.
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const uploadRef = useRef(upload);
    uploadRef.current = upload;
    const describeRef = useRef(describeError);
    describeRef.current = describeError;

    const active = pending.filter((p) => p.status !== "error").length;
    const room = Math.max(0, maxFiles - items.length - active);
    const busy = pending.some((p) => p.status === "queued" || p.status === "uploading");

    useEffect(() => {
        onBusyChange?.(busy);
    }, [busy, onBusyChange]);

    // Free preview URLs when the grid goes away.
    const pendingRef = useRef(pending);
    pendingRef.current = pending;
    useEffect(() => () => pendingRef.current.forEach((p) => URL.revokeObjectURL(p.preview)), []);

    const addFiles = useCallback(
        (files: File[], rejected: string[] = []) => {
            const problems = [...rejected];
            const accepted: File[] = [];
            for (const file of files) {
                if (!ACCEPTED_TYPES.includes(file.type)) {
                    problems.push(`${file.name || "That file"} isn't a JPG, PNG, WebP or GIF image.`);
                } else if (file.size > maxSizeMB * 1024 * 1024) {
                    problems.push(`${file.name || "That image"} is larger than ${maxSizeMB} MB.`);
                } else {
                    accepted.push(file);
                }
            }
            const fits = accepted.slice(0, room);
            if (accepted.length > fits.length) {
                problems.push(`You can add up to ${maxFiles} images — ${accepted.length - fits.length} weren't added.`);
            }
            setMessages(problems);
            if (fits.length === 0) return;
            setPending((prev) => [
                ...prev,
                ...fits.map((file) => ({
                    key: `upload-${++pendingSeq}`,
                    file,
                    preview: URL.createObjectURL(file),
                    status: "queued" as const,
                })),
            ]);
        },
        [room, maxFiles, maxSizeMB],
    );

    // Start queued uploads, CONCURRENCY at a time.
    useEffect(() => {
        const slots = CONCURRENCY - pending.filter((p) => p.status === "uploading").length;
        const toStart = pending.filter((p) => p.status === "queued").slice(0, Math.max(0, slots));
        if (toStart.length === 0) return;
        const starting = new Set(toStart.map((p) => p.key));
        setPending((prev) => prev.map((p) => (starting.has(p.key) ? { ...p, status: "uploading" } : p)));
        toStart.forEach((p) => {
            uploadRef
                .current(p.file)
                .then((result) =>
                    setPending((prev) => prev.map((x) => (x.key === p.key ? { ...x, status: "done", result } : x))),
                )
                .catch((err) =>
                    setPending((prev) =>
                        prev.map((x) => (x.key === p.key ? { ...x, status: "error", error: describeRef.current(err) } : x)),
                    ),
                );
        });
    }, [pending]);

    // Hand finished uploads to the parent in the order they were chosen.
    useEffect(() => {
        const ready: Pending<T>[] = [];
        for (const p of pending) {
            if (p.status === "done") ready.push(p);
            else if (p.status !== "error") break;
        }
        if (ready.length === 0) return;
        const readyKeys = new Set(ready.map((r) => r.key));
        onChangeRef.current([...itemsRef.current, ...ready.map((r) => r.result as T)]);
        ready.forEach((r) => URL.revokeObjectURL(r.preview));
        setPending((prev) => prev.filter((p) => !readyKeys.has(p.key)));
    }, [pending]);

    const retry = (key: string) =>
        setPending((prev) => prev.map((p) => (p.key === key ? { ...p, status: "queued", error: undefined } : p)));
    const dropPending = (key: string) =>
        setPending((prev) => {
            const gone = prev.find((p) => p.key === key);
            if (gone) URL.revokeObjectURL(gone.preview);
            return prev.filter((p) => p.key !== key);
        });

    // Paste screenshots straight in.
    useEffect(() => {
        if (!enablePaste || disabled) return;
        const onPaste = (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith("image/"));
            if (files.length === 0) return; // plain text paste — leave it alone
            e.preventDefault();
            addFiles(files);
        };
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
    }, [enablePaste, disabled, addFiles]);

    const onDrop = useCallback(
        (accepted: File[], rejections: FileRejection[]) => {
            const rejected = rejections.map(({ file, errors }) =>
                errors.some((e) => e.code === "file-too-large")
                    ? `${file.name} is larger than ${maxSizeMB} MB.`
                    : `${file.name} isn't a JPG, PNG, WebP or GIF image.`,
            );
            addFiles(accepted, rejected);
        },
        [addFiles, maxSizeMB],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPT,
        maxSize: maxSizeMB * 1024 * 1024,
        multiple: true,
        disabled: disabled || room === 0,
    });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = ({ active: a, over }: DragEndEvent) => {
        if (!over || a.id === over.id) return;
        const ids = items.map(getId);
        onChange(arrayMove(items, ids.indexOf(String(a.id)), ids.indexOf(String(over.id))));
    };

    return (
        <Stack spacing={1}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={items.map(getId)} strategy={rectSortingStrategy}>
                        {items.map((item, index) => (
                            <SortableTile
                                key={getId(item)}
                                id={getId(item)}
                                src={getThumbnail(item)}
                                label={getName?.(item) ?? `Image ${index + 1}`}
                                position={index + 1}
                                disabled={disabled}
                                onRemove={() => onChange(items.filter((x) => getId(x) !== getId(item)))}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                {pending.map((p) => (
                    <Tile key={p.key} src={p.preview} label={p.file.name}>
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                bgcolor: p.status === "error" ? "rgba(180, 35, 24, 0.82)" : "rgba(255,255,255,0.6)",
                                color: p.status === "error" ? "common.white" : "text.primary",
                                textAlign: "center",
                                px: 0.5,
                            }}
                        >
                            {p.status === "error" ? (
                                <>
                                    <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                                        {p.error}
                                    </Typography>
                                    <Stack direction="row">
                                        <Tooltip title="Try again">
                                            <IconButton size="small" onClick={() => retry(p.key)} sx={{ color: "inherit" }} aria-label={`Retry ${p.file.name}`}>
                                                <Refresh fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove">
                                            <IconButton size="small" onClick={() => dropPending(p.key)} sx={{ color: "inherit" }} aria-label={`Remove ${p.file.name}`}>
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </>
                            ) : (
                                <CircularProgress size={26} aria-label={`Uploading ${p.file.name}`} />
                            )}
                        </Box>
                    </Tile>
                ))}

                {room > 0 && !disabled && (
                    <Box
                        {...getRootProps()}
                        sx={{
                            width: TILE,
                            height: TILE,
                            borderRadius: 2,
                            border: "2px dashed",
                            borderColor: isDragActive ? "primary.main" : "divider",
                            bgcolor: isDragActive ? "action.hover" : "background.paper",
                            color: "text.secondary",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            textAlign: "center",
                            "&:hover": { borderColor: "primary.main", color: "primary.main" },
                            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                        }}
                        role="button"
                        aria-label="Add images"
                    >
                        <input {...getInputProps()} />
                        <AddPhotoAlternateOutlined />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {isDragActive ? "Drop here" : "Add images"}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary">
                Drop, choose{enablePaste ? " or paste (Ctrl/⌘+V)" : ""} · JPG, PNG, WebP or GIF · up to {maxSizeMB} MB each ·{" "}
                {items.length + active}/{maxFiles}
                {items.length > 1 ? " · drag ⠿ to reorder" : ""}
            </Typography>

            {messages.length > 0 && (
                <Alert severity="warning" onClose={() => setMessages([])} sx={{ py: 0 }}>
                    {messages.map((m) => (
                        <div key={m}>{m}</div>
                    ))}
                </Alert>
            )}
            {pending.some((p) => p.status === "error") && (
                <Button size="small" onClick={() => pending.filter((p) => p.status === "error").forEach((p) => retry(p.key))} sx={{ alignSelf: "flex-start" }}>
                    Retry failed uploads
                </Button>
            )}
        </Stack>
    );
}

const Tile: React.FC<{ src: string; label: string; children?: React.ReactNode; style?: React.CSSProperties; innerRef?: (el: HTMLElement | null) => void; dragging?: boolean }> = ({
    src,
    label,
    children,
    style,
    innerRef,
    dragging,
}) => (
    <Box
        ref={innerRef}
        style={style}
        sx={{
            position: "relative",
            width: TILE,
            height: TILE,
            borderRadius: 2,
            overflow: "hidden",
            border: 1,
            borderColor: "divider",
            bgcolor: "grey.100",
            boxShadow: dragging ? 6 : 0,
            zIndex: dragging ? 2 : "auto",
        }}
    >
        <Box component="img" src={src} alt={label} draggable={false} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        {children}
    </Box>
);

const overlayButtonSx = {
    position: "absolute" as const,
    width: 30,
    height: 30,
    bgcolor: "rgba(0,0,0,0.55)",
    color: "common.white",
    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
};

const SortableTile: React.FC<{
    id: string;
    src: string;
    label: string;
    position: number;
    disabled: boolean;
    onRemove: () => void;
}> = ({ id, src, label, position, disabled, onRemove }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    return (
        <Tile
            src={src}
            label={label}
            innerRef={setNodeRef}
            dragging={isDragging}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: 10,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "common.white",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                aria-hidden="true"
            >
                {position}
            </Box>
            {!disabled && (
                <>
                    <IconButton
                        size="small"
                        onClick={onRemove}
                        aria-label={`Remove ${label}`}
                        sx={{ ...overlayButtonSx, top: 4, right: 4 }}
                    >
                        <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        ref={setActivatorNodeRef}
                        {...attributes}
                        {...listeners}
                        aria-label={`Drag to reorder ${label} (position ${position})`}
                        sx={{ ...overlayButtonSx, bottom: 4, left: 4, cursor: "grab", touchAction: "none" }}
                    >
                        <DragIndicator sx={{ fontSize: 18 }} />
                    </IconButton>
                </>
            )}
        </Tile>
    );
};
